/**
 * THE AUDIO LANE, ROUND 1. What the host page is allowed to know about sound.
 *
 * -- WHAT THE GAME CAN NOW BE HEARD DOING ---------------------------------------
 *
 * A sea, continuously, made of two seamless loops of coprime length that never line up inside
 * three minutes. A landing, small, and heavier for a heavier fish, chosen by where that fish
 * falls in its OWN species range rather than against the table. A personal best. A lump of weed.
 * The sheet of paper coming up off the bottom edge and going back down, which is the one gesture
 * on screen that had never had a voice. And a mark on the paper when the player uses the single
 * verb the contract gives them.
 *
 * -- EVERY SAMPLE IS COMPUTED BY THIS REPOSITORY ---------------------------------
 *
 * No recording, no sample pack, no download, no dependency. The licence question this run asks
 * of every asset - where did this byte come from and who owns it - has one answer for the whole
 * lane: `spec.ts` and a seeded generator. That is a stronger position than a correctly-attributed
 * CC0 file, because a CC0 upload can turn out to be somebody else's recording and there is no way
 * to find that out afterwards.
 *
 * -- WHAT THE HOST HAS TO DO -----------------------------------------------------
 *
 *   1. `arm()` once, at start-up. It constructs nothing.
 *   2. `observe(state)` on every look at the save. It diffs and sounds what changed, timed to
 *      the catch ceremony's phases (the landing at the pull, the record chime at the reveal).
 *   3. `sheet(up)` and `mark()` when those two things happen on screen; `mark()` also on Reel.
 *   4. `setMix({ master, sea, effects, muted })` with the profile's settings, on load and on
 *      change; `toggleMuted()` on the key.
 *
 * There is no update call and no per-frame anything. If the host stops calling `observe` the sea
 * carries on, because the sea is not a function of the host.
 */

export { createAudioEngine, type AudioEngine, type AudioMix, type AudioStats } from './engine';
export { eventsBetween, snapshotOf, weightFractionOf, type AudioEvent, type AudioSnapshot } from './events';
export { captureOffline, type CaptureMoment, type CaptureOptions } from './capture';
export { measureSignal, measureSeam, measureJoinAt, mixLayers, bandLabels, BAND_EDGES, type SignalReport, type SeamReport } from './measure';
export { renderSea, renderSurface, renderSwell, type SeaLayer } from './sea';
export { renderMomentBank, type MomentBank, type MomentKind } from './moments';
export { LAND_MIN_GAP_MS, MUTE_KEY, SETTLE_BURST_MAX, SURFACE, SWELL, TOLD_KEY } from './spec';

/**
 * THE TWO LINES THE CHART SPEAKS ABOUT SOUND, and they are the only pixels this lane costs.
 *
 * -- WHY THERE IS NO DRAWN CONTROL -----------------------------------------------
 *
 * The chart already refuses to print an affordance for opening itself, and says why in
 * world-entry.ts: a hint painted on the paper would be the permanent chrome the whole design is
 * arranged to avoid. A speaker glyph would be the first permanent mark on a sheet whose palette
 * is five values, whose written roles are two, and whose only ornament is the torn edge. The
 * owner has a standing rejection on record for exactly this shape of mistake - every capability
 * given its own permanent surface instead of being revealed when it is relevant - and it is
 * scoped to bind everywhere, not to the project it happened on.
 *
 * So sound is revealed when it is relevant and never again: one line, the first time it starts,
 * through the same transient aside the sheet already answers the player with, and then one line
 * on each toggle. It leaves after four seconds like every other thing that line says, and
 * nothing about it becomes a standing counter.
 *
 * The register is the sheet's own: short, plain, no boast about what was built. "Now fishing for
 * cod." is the neighbouring sentence, and these two are cut to the same length.
 */
export const AUDIO_LINES = {
  /** Said once per browser, the first time the sea starts. */
  first: 'Sound on. S for quiet.',
  quiet: 'Quiet. S for sound.',
  loud: 'Sound on. S for quiet.',
} as const;
